import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api';

const LARS_STAGES = [
    { value: 1, label: '1 — Data Preparation' },
    { value: 2, label: '2 — Query Execution' },
    { value: 3, label: '3 — Scoring' },
    { value: 4, label: '4 — Reporting' },
];

const CreateGlobalTask: React.FC = () => {
    const [frequency, setFrequency] = useState('');
    const [scheduleName, setScheduleName] = useState('');
    const [selectedStages, setSelectedStages] = useState<number[]>([1, 2, 3, 4]);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const isLarsTask = scheduleName === 'run_lars_stages';

    const toggleStage = (stage: number) => {
        setSelectedStages((prev) =>
            prev.includes(stage) ? prev.filter((s) => s !== stage) : [...prev, stage].sort((a, b) => a - b)
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        if (!frequency || !scheduleName) {
            setError('Task type and frequency are required.');
            setIsLoading(false);
            return;
        }

        if (isLarsTask && selectedStages.length === 0) {
            setError('Select at least one LARS stage to run.');
            setIsLoading(false);
            return;
        }

        try {
            const extra_data = isLarsTask
                ? {
                      stages: selectedStages,
                      stop_on_failure: true,
                      sync_report: true,
                  }
                : {};

            const response = await apiClient.createGlobalTask({
                schedule_name: scheduleName,
                frequency,
                extra_data,
            });
            if (response.success === false) {
                setError(response.message || 'Failed to create global task.');
                return;
            }
            navigate('/tasks');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to create global task.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4">Create Global Task</h1>
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
                    {error}
                </div>
            )}
            <form onSubmit={handleSubmit} className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 max-w-lg">
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="scheduleName">
                        Task Type
                    </label>
                    <select
                        id="scheduleName"
                        className="shadow border rounded w-full py-2 px-3 text-gray-700"
                        value={scheduleName}
                        required
                        onChange={(e) => setScheduleName(e.target.value)}
                        disabled={isLoading}
                    >
                        <option value="">Select task type</option>
                        <option value="regenerate_all_embeddings">Regenerate All Embeddings</option>
                        <option value="run_lars_stages">Run LARS Stages</option>
                    </select>
                </div>

                {isLarsTask && (
                    <div className="mb-4">
                        <span className="block text-gray-700 text-sm font-bold mb-2">Stages to run</span>
                        <div className="space-y-2 border rounded px-3 py-3 bg-gray-50">
                            {LARS_STAGES.map((stage) => (
                                <label key={stage.value} className="flex items-center gap-2 text-sm text-gray-800">
                                    <input
                                        type="checkbox"
                                        checked={selectedStages.includes(stage.value)}
                                        onChange={() => toggleStage(stage.value)}
                                        disabled={isLoading}
                                    />
                                    {stage.label}
                                </label>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            Stages run in order. On failure, later stages are skipped so you can re-run manually from Labs.
                        </p>
                    </div>
                )}

                <div className="mb-6">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="frequency">
                        Frequency
                    </label>
                    <select
                        id="frequency"
                        className="shadow border rounded w-full py-2 px-3 text-gray-700"
                        value={frequency}
                        required
                        onChange={(e) => setFrequency(e.target.value)}
                        disabled={isLoading}
                    >
                        <option value="">Select frequency</option>
                        <option value="monthly">Monthly</option>
                        <option value="biweekly">Bi-Weekly</option>
                        <option value="weekly">Weekly</option>
                        <option value="daily">Daily</option>
                    </select>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                    All active tenants are included by default. You can deactivate individual tenants on the task detail page.
                </p>

                <div className="flex items-center justify-between">
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                    >
                        {isLoading ? 'Creating...' : 'Create Global Task'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/tasks')}
                        className="text-sm text-primary-600 hover:text-primary-800"
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateGlobalTask;
